// frontend/src/auth/ResetPassword.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './ResetPassword.css';

export default function ResetPassword() {
  const { state } = useLocation();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Verificar código, 2: Nueva contraseña
  const [formData, setFormData] = useState({
    email: state?.email || '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Redirigir si no hay email
  useEffect(() => {
    if (!state?.email) {
      navigate('/forgot-password');
    }
  }, [state, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // Paso 1: Verificar código
  const verifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.code.length !== 6) {
      setError('El código debe tener 6 dígitos');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.verifyResetCode, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          code: formData.code
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Código inválido');
      }

      console.log('✅ Código verificado');
      setSuccess('Código verificado correctamente');
      
      // Avanzar al siguiente paso
      setTimeout(() => {
        setStep(2);
        setSuccess('');
      }, 1000);

    } catch (err) {
      console.error('❌ Error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: Cambiar contraseña
  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validaciones
    if (formData.newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.resetPassword, {


        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          code: formData.code,
          newPassword: formData.newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al cambiar la contraseña');
      }

      console.log('✅ Contraseña actualizada');
      setSuccess('¡Contraseña actualizada exitosamente!');

      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Contraseña actualizada. Inicia sesión con tu nueva contraseña' }
        });
      }, 2000);

    } catch (err) {
      console.error('❌ Error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-container">
        <div className="reset-card">
          {/* Progress Bar */}
          <div className="progress-bar">
            <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
              <div className="step-circle">1</div>
              <span className="step-label">Verificar código</span>
            </div>
            <div className="progress-line" />
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
              <div className="step-circle">2</div>
              <span className="step-label">Nueva contraseña</span>
            </div>
          </div>

          {/* Header */}
          <div className="reset-header">
            <div className="header-icon">
              {step === 1 ? '🔐' : '🔑'}
            </div>
            <h2 className="reset-title">
              {step === 1 ? 'Verificar código' : 'Nueva contraseña'}
            </h2>
            <p className="reset-subtitle">
              {step === 1 
                ? `Ingresa el código enviado a ${formData.email}`
                : 'Crea una contraseña segura para tu cuenta'
              }
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              <span>{success}</span>
            </div>
          )}

          {/* Formulario Paso 1: Verificar código */}
          {step === 1 && (
            <form onSubmit={verifyCode} className="reset-form">
              <div className="form-group">
                <label htmlFor="code">Código de verificación</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="000000"
                  maxLength="6"
                  required
                  disabled={loading}
                  autoFocus
                  className="code-input"
                />
                <small className="input-hint">
                  Revisa tu correo electrónico
                </small>
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Verificando...
                  </>
                ) : (
                  'Verificar código'
                )}
              </button>

              <div className="resend-section">
                <p>¿No recibiste el código?</p>
                <button 
                  type="button" 
                  className="resend-button"
                  onClick={() => navigate('/forgot-password', { state: { email: formData.email } })}
                >
                  Reenviar código
                </button>
              </div>
            </form>
          )}

          {/* Formulario Paso 2: Nueva contraseña */}
          {step === 2 && (
            <form onSubmit={resetPassword} className="reset-form">
              <div className="form-group">
                <label htmlFor="newPassword">Nueva contraseña</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  minLength="6"
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar contraseña</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repite la contraseña"
                  minLength="6"
                  required
                  disabled={loading}
                />
              </div>

              <div className="password-tips">
                <p className="tips-title">Tu contraseña debe contener:</p>
                <ul className="tips-list">
                  <li className={formData.newPassword.length >= 6 ? 'valid' : ''}>
                    ✓ Al menos 6 caracteres
                  </li>
                  <li className={formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '' ? 'valid' : ''}>
                    ✓ Ambas contraseñas coinciden
                  </li>
                </ul>
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Actualizando...
                  </>
                ) : (
                  'Actualizar contraseña'
                )}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="reset-footer">
            <button 
              className="back-link"
              onClick={() => navigate('/login')}
            >
              ← Volver al login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}